class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const gridContainer = document.getElementById('ProductGridContainer');
    const grid = gridContainer && gridContainer.querySelector('.collection');
    if (grid) grid.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
    FacetFiltersForm.abortController = new AbortController();

    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const target = document.getElementById('ProductGridContainer');
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (!target || !source) return;

    morph(target, source, MORPH_OPTIONS);

    target.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (source && container) {
      container.innerHTML = source.innerHTML;
      container.classList.remove('loading');
      if (containerDesktop) {
        containerDesktop.innerHTML = source.innerHTML;
        containerDesktop.classList.remove('loading');
      }
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const roots = ['#FacetFiltersForm', '#FacetFiltersFormMobile', '#FacetFiltersPillsForm'];

    for (const rootSelector of roots) {
      const target = document.querySelector(rootSelector);
      const source = parsedHTML.querySelector(rootSelector);
      if (!target || !source) continue;

      FacetFiltersForm.preserveShowMore(target, source);
      morph(target, source, MORPH_OPTIONS);
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.restoreFocus(event);
  }

  static preserveShowMore(target, source) {
    target.querySelectorAll('.js-filter').forEach((filter) => {
      if (!filter.id) return;
      if (!filter.querySelector('show-more-button .label-show-more.hidden')) return;

      const incoming = source.querySelector(`#${CSS.escape(filter.id)}`);
      if (!incoming) return;

      incoming
        .querySelectorAll('.facets__item.hidden')
        .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
    });
  }

  static restoreFocus(event) {
    if (!event || !event.target || typeof event.target.closest !== 'function') return;

    const jsFilter = event.target.closest('.js-filter');
    if (!jsFilter || !jsFilter.id) return;

    const current = document.getElementById(jsFilter.id);
    if (!current) return;

    const panel = current.querySelector('.facets__display-vertical');
    if (current.hasAttribute('open') && panel && getComputedStyle(panel).position === 'fixed') return;

    const selector = current.classList.contains('mobile-facets__details')
      ? '.mobile-facets__close-button'
      : '.facets__summary';
    const elementToActivate = current.querySelector(selector);

    const type = event.target.getAttribute('type');
    const keepsFocus = type === 'text' || type === 'range' || type === 'number';
    if (elementToActivate && !keepsFocus) elementToActivate.focus();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!activeFacetsElement || !target) return;
      target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const drawer = mobileForm && mobileForm.closest('menu-drawer');
    if (drawer && typeof drawer.bindEvents === 'function') drawer.bindEvents();
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  connectedCallback() {
    if (!this.listenersAttached) {
      this.listenersAttached = true;
      this.addEventListener('input', this.onHandleInput.bind(this));
      this.addEventListener('pointerdown', this.onPointerDown.bind(this));
    }
    this.sync();
  }

  morphedCallback() {
    this.sync();
  }

  get root() {
    return this.querySelector('[data-price-range]');
  }

  get minHandle() {
    return this.querySelector('[data-price-handle="min"]');
  }

  get maxHandle() {
    return this.querySelector('[data-price-handle="max"]');
  }

  get rangeMax() {
    const root = this.root;
    const value = root ? Number(root.dataset.max) : NaN;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  onPointerDown(event) {
    const slider = this.querySelector('.price-range__slider');
    const minHandle = this.minHandle;
    const maxHandle = this.maxHandle;
    if (!slider || !minHandle || !maxHandle) return;

    const bounds = slider.getBoundingClientRect();
    if (!bounds.width) return;

    const ratio = (event.clientX - bounds.left) / bounds.width;
    const value = ratio * this.rangeMax;
    const closerToMax = Math.abs(value - Number(maxHandle.value)) < Math.abs(value - Number(minHandle.value));

    minHandle.style.zIndex = closerToMax ? '1' : '3';
    maxHandle.style.zIndex = closerToMax ? '3' : '1';
  }

  onHandleInput(event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-price-handle]')) return;

    this.clamp(target.dataset.priceHandle);
    this.sync();
  }

  clamp(moved) {
    const minHandle = this.minHandle;
    const maxHandle = this.maxHandle;
    if (!minHandle || !maxHandle) return;

    const min = Number(minHandle.value);
    const max = Number(maxHandle.value);
    if (min < max) return;

    if (moved === 'max') {
      maxHandle.value = String(Math.min(this.rangeMax, min + 1));
    } else {
      minHandle.value = String(Math.max(0, max - 1));
    }
  }

  sync() {
    const root = this.root;
    const minHandle = this.minHandle;
    const maxHandle = this.maxHandle;
    if (!root || !minHandle || !maxHandle) return;

    const rangeMax = this.rangeMax;
    const min = Number(minHandle.value);
    const max = Number(maxHandle.value);

    const minField = this.querySelector('[data-price-field="min"]');
    const maxField = this.querySelector('[data-price-field="max"]');
    if (minField) minField.value = min > 0 ? String(min) : '';
    if (maxField) maxField.value = max < rangeMax ? String(max) : '';

    const minOutput = this.querySelector('[data-price-output="min"]');
    const maxOutput = this.querySelector('[data-price-output="max"]');
    if (minOutput) minOutput.textContent = this.formatMoney(min, root);
    if (maxOutput) maxOutput.textContent = this.formatMoney(max, root);

    root.style.setProperty('--price-min-ratio', String(min / rangeMax));
    root.style.setProperty('--price-max-ratio', String(max / rangeMax));

    minHandle.setAttribute('aria-valuetext', this.formatMoney(min, root));
    maxHandle.setAttribute('aria-valuetext', this.formatMoney(max, root));
  }

  formatMoney(value, root) {
    const currency = root.dataset.currency;
    const locale = root.dataset.locale || document.documentElement.lang || undefined;

    if (currency) {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }).format(value);
      } catch (error) {
      }
    }

    return `${value}${root.dataset.currencySymbol ? ` ${root.dataset.currencySymbol}` : ''}`;
  }
}

customElements.define('price-range', PriceRange);

(function () {
  const SHEET_ANIMATION_MS = 300;
  const CLOSING_CLASS = 'facets--sheet-closing';

  let scrollLocked = false;

  function panelOf(details) {
    return details.querySelector('.facets__display-vertical');
  }

  function isSheet(details) {
    const panel = panelOf(details);
    if (!panel) return false;
    return getComputedStyle(panel).position === 'fixed';
  }

  function containerOf(details) {
    return details.closest('.facets-container');
  }

  function openSheets(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll('details.js-filter[open]'));
  }

  function syncOverlay(container) {
    if (!container) return;
    const overlay = container.querySelector('[data-facets-sheet-overlay]');
    if (!overlay) return;

    const anyOpen = openSheets(container).some((details) => isSheet(details));
    if (anyOpen) {
      overlay.setAttribute('data-facets-sheet-open', 'true');
      if (!scrollLocked) {
        document.body.classList.add('overflow-hidden');
        scrollLocked = true;
      }
    } else {
      overlay.removeAttribute('data-facets-sheet-open');
      if (scrollLocked) {
        document.body.classList.remove('overflow-hidden');
        scrollLocked = false;
      }
    }
  }

  function summaryOf(details) {
    return details.querySelector('.facets__summary');
  }

  function syncExpanded(details) {
    const summary = summaryOf(details);
    if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  }

  function markPanelAsDialog(details, active) {
    const panel = panelOf(details);
    if (!panel) return;

    if (!active) {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('aria-label');
      return;
    }

    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    const summary = summaryOf(details);
    if (summary) panel.setAttribute('aria-label', summary.textContent.trim());
  }

  function closeSheet(details) {
    if (!details.open) return;

    if (!isSheet(details)) {
      details.open = false;
      syncExpanded(details);
      return;
    }

    if (details.classList.contains(CLOSING_CLASS)) return;

    const container = containerOf(details);
    details.classList.add(CLOSING_CLASS);
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    markPanelAsDialog(details, false);

    setTimeout(() => {
      details.classList.remove(CLOSING_CLASS);
      details.open = false;
      details.removeAttribute('open');
      syncExpanded(details);
      syncOverlay(container);
    }, SHEET_ANIMATION_MS);
  }

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLElement) || details.tagName !== 'DETAILS') return;
      if (!details.classList.contains('js-filter')) return;

      syncExpanded(details);

      if (details.open && isSheet(details)) {
        openSheets(containerOf(details)).forEach((other) => {
          if (other !== details) closeSheet(other);
        });

        markPanelAsDialog(details, true);
        const panel = panelOf(details);
        if (panel && typeof trapFocus === 'function') trapFocus(panel);
      } else if (!details.open) {
        markPanelAsDialog(details, false);
      }

      syncOverlay(containerOf(details));
    },
    true
  );

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const overlay = target.closest('[data-facets-sheet-overlay]');
    if (overlay) {
      event.preventDefault();
      openSheets(overlay.closest('.facets-container')).forEach(closeSheet);
      return;
    }

    const closer = target.closest('[data-facets-sheet-close]');
    if (!closer) return;

    const details = closer.closest('details.js-filter');
    if (!details) return;

    event.preventDefault();
    closeSheet(details);

    const summary = summaryOf(details);
    if (summary) summary.focus();
  });

  let resyncQueued = false;
  window.addEventListener('resize', () => {
    if (resyncQueued) return;
    resyncQueued = true;
    requestAnimationFrame(() => {
      resyncQueued = false;
      document.querySelectorAll('.facets-container').forEach(syncOverlay);
    });
  });

  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll('.facets-container').forEach((container) => {
      openSheets(container).forEach((details) => {
        if (isSheet(details)) closeSheet(details);
      });
    });
  });
})();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
